<template>
  <tr class="tt form-common-style">
    <td class="tt__col tt__col-1">
      <span class="tt__type">{{
        ticketInfo.priceClassAlias.priceClassAlias
      }}</span>
    </td>
    <td class="tt__col tt__col-2">
      <span class="tt__price">{{ formattedPrice(ticketInfo.priceValueAmount.amount) }}</span>
    </td>
    <td class="tt__col tt__col-3 tt__ticket-quantity-wrapper ">
      <div class="ticket-quantity">
        <div class="ticket-quantity__wrapper ticket-quantity__decrement-wrapper">
          <span class="sr-only">{{ $t("ticket.remove_ticket") }}</span>
          <font-awesome-icon icon="minus-square" 
            @click="decrementTicket"
            v-if="noOfTicketsSelected > 0" />
        </div>
        <input
          class="ticket-quantity__input"
          :class="{ 'border--error': showTicketTypeError }"
          type="number"
          v-model="noOfTicketsSelected"
          placeholder="0"
        />
        <div class="ticket-quantity__wrapper ticket-quantity__increment-wrapper">
          <span class="sr-only">{{ $t("ticket.add_ticket") }}</span>
          <font-awesome-icon icon="plus-square" @click="incrementTicket" />
        </div>
      </div>
    </td>
    <td class="tt__discount-wrapper input-wrapper" v-if="discountFieldShown">
      <input class="input" 
        type="text"
        required
        v-model="discountCode"
        :placeholder="$t('ticket.discount_code')"
        :class="{ 'border--error': !validDiscountCodeFormat }"
        :id="ticketInfo.priceClassId"
      />
      <label
        class="floating-label"
        v-t="'ticket.discount_code'"
        :for="ticketInfo.priceClassId"
      />
    </td>
    <error v-if="(!validDiscountCodeFormat && discountFieldShown) && showDiscountError">{{ $t('errors.invalid_discount_code_format') }}</error>
    <error v-show="!selectionInAvailableQuantity">{{ $t('errors.ticket_type_available_quantity') }} {{ formattedAvailableQuantity }}</error>
  </tr>
</template>

<script>
import Error from "@/components/generic/Error";
import { mapState } from 'vuex';
import Helpers from "@/util/helpers"

export default {
  name: "ticket-item",
  mixins: [Helpers],
  components: {
    Error,
  },
  data() {
    return {
      noOfTicketsSelected: "",
      discountCode: "",
      showError: false,
      showDiscountError: false
    };
  },
  props: {
    ticketInfo: {
      type: Object,
      required: true,
    },
    ticketsRequired: {
      type: Number,
      required: false,
      default: null,
    },
    ticketCount: {
      type: Number,
      required: true,
      default: null,
    },
    ticketTypeLastModified: {
      type: String,
      required: true,
    },
    priceClassCode: {
      type: String,
      required: true
    },
    passwordRequired: {
      type: Number,
      required: true
    },
  },
  methods: {
    incrementTicket() {
      if(this.noOfTicketsSelected < 40) {
        this.noOfTicketsSelected++;
      }

      this.checkForErrors();
    },
    decrementTicket() {
      if(this.noOfTicketsSelected > 0) {
        this.noOfTicketsSelected--;
      }

      this.checkForErrors();
    },
    showTicketTypeDetails(ticketTypeName) {
      this.$emit("showTicketInfoModal", {
        ticketType: ticketTypeName,
      });
    },
    validDiscountCodeFormatCheck() {
      if ((!this.validDiscountCodeFormat || !this.discountCode.length) && this.discountFieldShown) {
        this.showDiscountError = true;
      }
    },
    checkForErrors() {
      if(this.maximumExceeded || this.showTicketTypeError || !(this.selectionInAvailableQuantity)) {
        this.$emit('errorFound', this.priceClassCode)
      } else {
        this.$emit('clearTicketError', this.priceClassCode)
      }
    }
  },
  watch: {
    noOfTicketsSelected() {
      // Reset error when the field in updated.
      this.maxAllowedError = false;

      if (this.noOfTicketsSelected < 0) {
        this.noOfTicketsSelected = "";
      } else {
        this.$emit("updateTicketsSelected", {
          quantity: this.noOfTicketsSelected ? this.noOfTicketsSelected : 0,
          ticket_type: this.priceClassCode,
          discountCode: this.discountCode,
          ticketTypeModified: this.ticketInfo.priceClassAlias.priceClassAlias,
          discountFieldShown: this.discountFieldShown,
        });
      }
    },
    discountCode() {
      this.validDiscountCodeFormatCheck();

      this.$emit("ticketTypeErrorUpdate", {
        discountError: !this.validDiscountCodeFormat,
        maxAllowedError: this.maximumExceeded,
      });

      this.$emit("updateTicketsSelected", {
        quantity: this.noOfTicketsSelected ? this.noOfTicketsSelected : 0,
        ticket_type: this.priceClassCode,
        discountCode: this.discountCode,
        ticketTypeModified: this.ticketInfo.priceClassAlias.priceClassAlias,
        discountFieldShown: this.discountFieldShown,
      });
    },
  },
  computed: {
    validDiscountCodeFormat() {
      // Invalid if there is less then 3 characters or more than 15 added.
      if ((this.discountCode.length > 0 && this.discountCode.length < 3) || this.discountCode.length > 15) {
        return false;
      }
      return true;
    },
    formattedAvailableQuantity() {
      return this.ticketInfo.availableQty.split(',').join(', ');
    },
    discountFieldShown() {
      if (this.passwordRequired === 1 && this.noOfTicketsSelected > 0) {
        return true;
      }
      // Clean up the discount code field when hidden.
      this.discountCode = "";
      return false;
    },
    maxAllowed() {
      let availableQty = parseInt(
        this.ticketInfo.availableQty.split(",").pop()
      );

      let max = 0;

      if (this.performance.mode != "GA") {
        // User can only pick as many tickets of that type as are available
        // Even if they've chosen more seats
        if(availableQty < this.ticketsRequired) {
          max = availableQty
        } else {
          max = this.ticketsRequired;
        }
      } else if (availableQty && availableQty > 0 && availableQty <= 40) {
        // If the availablity is set on the ticket type and doesn't exceed 40.
        max = availableQty;
      } else {
        // Default Sistic max is 40 per event.
        max = 40;
      }
      return max;
    },
    selectionInAvailableQuantity() {
      if(this.noOfTicketsSelected == 0) {
        return true
      } else {
        // Search through availableQty, see if ticket selection matches this
        return this.ticketInfo.availableQty.split(',').some((item) => {
          return this.noOfTicketsSelected == item;
        });
      }
    },
    maximumExceeded() {
      return this.noOfTicketsSelected > this.maxAllowed
    },
    showTicketTypeError() {
      this.validDiscountCodeFormatCheck();
      // Check against the updated ticket type and an available error.
      if (this.ticketTypeLastModified === this.ticketInfo.priceClassAlias.priceClassAlias && this.showError === true) {
        this.$emit("ticketTypeErrorUpdate", {
          discountError: !this.validDiscountCodeFormat,
          maxAllowedError: this.maxAllowed,
        });

        return true;
      }

      return false;
    },
    maxSeatsMessage() {
      let message = this.$i18n.tc(
        "errors.max_allowed_tickets",
        this.maxAllowed
      );
      return message;
    },
    ...mapState(['performance'])
  },
};
</script>
